<template>
  <MazPhoneNumberInput
    :id="id"
    ref="phoneInputWrapper"
    class="maz-phone-input"
    :class="{ 'theme-light': color === 'light' }"
    :country-locale="countryLocale"
    @update="handleUpdate"
    @vue:before-unmount="removeMoutedAttributes"
    @vue:mounted="addMoutedAttributes"
  />
</template>

<script setup lang="ts">
// component wraps up the MazPhoneNumberInput because it's the only element all attributes added when using this componet passthrough to the maz component.
import type { Results } from "maz-ui/components/MazPhoneNumberInput";
import MazPhoneNumberInput from "maz-ui/components/MazPhoneNumberInput";
// importing root values required for other css to work, can't scope these root values, using vite import to prevent dupes at bundle time
import "~/assets/scss/maz-custom/maz-root-values.scss";

const props = withDefaults(
  defineProps<{
    id: string;
    color?: "dark" | "light";
  }>(),
  {
    color: "light"
  }
);

// Without an explicit country-locale, maz-ui falls back to window.navigator.language and
// hands it straight to new Intl.DisplayNames() to name the countries. Browser/OS setups
// that report a non-BCP-47 value (e.g. "united states-UNITED STATES") make that
// constructor throw a RangeError while the country list renders, which takes down the
// whole RFI form. Validating the browser value keeps country names localized for
// visitors abroad and falls back to English only when the browser reports garbage.
// Resolved after mount so the prerendered markup and the hydrated markup agree.
const countryLocale = ref(FALLBACK_COUNTRY_LOCALE);

onMounted(() => {
  countryLocale.value = resolveCountryLocale([
    window.navigator.language,
    ...(window.navigator.languages ?? [])
  ]);
});

const attrs = useAttrs();
const phoneInput = ref<HTMLInputElement | null | undefined>();

const emits = defineEmits<{
  (e: "blur"): void;
  (e: "update", results: Results): void;
}>();

const handleBlur = (event: FocusEvent) => {
  const nextFocusedElement = event.relatedTarget;
  const phoneInputRoot = phoneInput.value?.closest(".m-phone-number-input");

  if (nextFocusedElement instanceof Node && phoneInputRoot?.contains(nextFocusedElement)) {
    return;
  }

  emits("blur");
};
const handleUpdate = (results: Results) => {
  emits("update", results);
};

const syncInputAttributes = () => {
  if (!phoneInput.value) {
    return;
  }

  const forwardedAttributes = ["aria-invalid", "aria-describedby", "aria-errormessage"] as const;

  for (const attribute of forwardedAttributes) {
    const value = attrs[attribute];

    if (typeof value === "string" && value) {
      phoneInput.value.setAttribute(attribute, value);
      continue;
    }

    phoneInput.value.removeAttribute(attribute);
  }
};

const addMoutedAttributes = () => {
  if (window) {
    phoneInput.value = document.getElementById(props.id) as HTMLInputElement;

    if (phoneInput.value) {
      // adding blur event listener
      phoneInput.value.addEventListener("blur", handleBlur);
      // adding data-cy for testing, attributes don't pass down when using maz
      phoneInput.value.setAttribute("data-cy", "rfi-phone-number");
      syncInputAttributes();
    }
  }
};

const removeMoutedAttributes = () => {
  if (window && phoneInput.value) {
    phoneInput.value.removeEventListener("blur", handleBlur);
  }
};

watch(
  () => [attrs["aria-invalid"], attrs["aria-describedby"], attrs["aria-errormessage"]],
  async () => {
    await nextTick();
    syncInputAttributes();
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
:deep() {
  @import "~/assets/scss/maz-custom/maz-stripped-down";
  button {
    background-color: transparent;
  }
  button,
  input,
  optgroup,
  select,
  textarea {
    font-family: inherit;
    font-size: 1em;
    line-height: 1.15;
    margin: 0;
  }
}

:deep(.m-country-selector__country-flag) {
  width: 24px !important;
}

.theme-light :deep() {
  --maz-color-success-600: #446d12 !important;
  --maz-color-success: #446d12 !important;
}
</style>
